import { NavLink, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useClientProject } from "../../contexts/ClientProjectContext";
import { BrandMark } from "../../components/Logo";
import { Button } from "../../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { LayoutDashboard, Boxes, ClipboardList, CalendarCheck, LogOut } from "lucide-react";
import { WhatsAppSupport } from "../../components/WhatsAppSupport";

const tabs = [
  { to: "/client", label: "Overview", icon: LayoutDashboard, end: true, key: "overview" },
  { to: "/client/attendance", label: "Attendance", icon: CalendarCheck, key: "attendance" },
  { to: "/client/stock", label: "Stock report", icon: Boxes, key: "stock" },
  { to: "/client/surveys", label: "Survey report", icon: ClipboardList, key: "surveys" },
];

export default function ClientLayout() {
  const { user, logout } = useAuth();
  const { projects, projectId, setProjectId } = useClientProject();
  const loc = useLocation();
  const current = tabs.find((t) => (t.end ? loc.pathname === t.to : loc.pathname.startsWith(t.to)));

  return (
    <div className="min-h-screen">
      <header className="sticky top-0 z-30 glass-bar">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-8 py-2.5 sm:py-0 sm:h-16 flex flex-wrap items-center gap-y-2">
          <div className="order-1">
            <BrandMark compact />
          </div>
          <div className="order-2 sm:order-3 ml-auto sm:ml-0 flex items-center gap-2 sm:gap-3">
            <div className="hidden md:block text-right leading-tight">
              <div className="text-xs font-medium" data-testid="client-username">
                {user?.name}
              </div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-neutral-500">
                {user?.company || "Client"}
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={logout}
              className="rounded-xl border-slate-300/70 h-9"
              data-testid="client-logout"
            >
              <LogOut className="h-4 w-4 sm:mr-1" />
              <span className="hidden sm:inline">Logout</span>
            </Button>
          </div>
          <div className="order-3 sm:order-2 w-full sm:w-auto sm:ml-auto sm:mr-3">
            <Select value={projectId} onValueChange={setProjectId}>
              <SelectTrigger
                className="rounded-full h-9 w-full sm:w-[280px] font-medium"
                data-testid="client-project-switcher"
              >
                <SelectValue placeholder="All projects" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all">All projects</SelectItem>
                {projects.map((p) => (
                  <SelectItem key={p.id} value={p.id} data-testid={`client-project-${p.id}`}>
                    <span className="font-medium">{p.name}</span>
                    <span className="ml-2 font-mono text-[10px] text-neutral-500">
                      {p.start_date} → {p.end_date}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <nav className="max-w-[1400px] mx-auto px-4 sm:px-8 flex gap-1 py-2 overflow-x-auto">
          {tabs.map((t) => (
            <NavLink
              key={t.key}
              to={t.to}
              end={!!t.end}
              data-testid={`client-nav-${t.key}`}
              className={({ isActive }) =>
                `flex items-center gap-1.5 sm:gap-2 px-3 py-1.5 sm:px-4 sm:py-2 text-[11px] sm:text-xs uppercase tracking-widest font-medium rounded-full transition-colors whitespace-nowrap ${
                  isActive
                    ? "bg-slate-900 text-white shadow-md"
                    : "text-slate-500 hover:text-slate-900 hover:bg-white/70"
                }`
              }
            >
              <t.icon className="h-4 w-4" />
              {t.label}
            </NavLink>
          ))}
        </nav>
        {current && (
          <div className="max-w-[1400px] mx-auto px-4 sm:px-8 py-2 border-t border-slate-200/50 text-[10px] font-mono uppercase tracking-widest text-neutral-500">
            <span className="text-black">{current.label}</span>
          </div>
        )}
      </header>
      <main className="max-w-[1400px] mx-auto px-4 sm:px-8 py-6 space-y-6">
        <Outlet />
        <div className="pt-4 flex justify-center">
          <WhatsAppSupport />
        </div>
      </main>
    </div>
  );
}
